<template>
  <div>
    <CRow class="justify-content-between mb-3">
      <CCol col="auto">
        <CDropdown
          :togglerText="this.$t('producer.products.editBtn.title')"
          color="primary"
          :disabled="!canView(PERMS.ORDERS_EDIT)"
        >
          <CDropdownItem @click="setDelivered()">{{
            $t(
              "role.orders.manage.ddmp.detail.orders.table.actions.markAsDelivered"
            )
          }}</CDropdownItem>
          <CDropdownItem @click="setNotDelivered()">{{
            $t(
              "role.orders.manage.ddmp.detail.delivery.table.products.table.actions.markAsNonDelivered"
            )
          }}</CDropdownItem>
          <CDropdownItem @click="createIncident(INCIDENT_TYPE.INCIDENT)">{{
            $t("role.orders.manage.ddmp.orderActions.openIncident")
          }}</CDropdownItem>

          <CDropdownItem @click="createIncident(INCIDENT_TYPE.CANCELLATION)">{{
            $t("common.editor.button.cancel")
          }}</CDropdownItem>
        </CDropdown>
      </CCol>
      <CCol col="auto">
        <OrdersPrints
          :prints="ddmi.prints"
          :title="
            this.$t(
              'role.orders.manage.ddmp.detail.delivery.table.products.printBtn'
            )
          "
          :types="['island_orders']"
        />
      </CCol>
    </CRow>

    <ejs-grid
      ref="grid"
      :dataSource="dm"
      :allowSelection="true"
      :selectionSettings="selectionSettings"
      :query="query"
      :detailTemplate="detailTemplate"
      :dataBound="onDataBound"
      @rowSelected="rowSelectedHandler($event)"
      @rowDeselected="rowDeselectedHandler($event)"
      @detailDataBound="onDetailDataBound"
    >
      <e-columns>
        <e-column type="checkbox" width="40" textAlign="left"></e-column>
        <e-column
          isPrimaryKey="{true}"
          field="id"
          :headerText="this.$t('role.orders.rounds.ddmi.details.table.account')"
          width="80"
          :template="accountTemplate"
        ></e-column>
        <e-column
          field="order.order.id"
          :headerText="
            this.$t(
              'role.orders.manage.ddmp.detail.delivery.table.products.table.name'
            )
          "
          :template="nameTemplate"
          width="200"
        ></e-column>
        <e-column
          field="info"
          :headerText="this.$t('role.orders.rounds.ddmi.details.table.info')"
          :template="infoTemplate"
        ></e-column>
        <e-column
          field="price"
          textAlign="right"
          :headerText="
            this.$t(
              'role.orders.manage.ddmp.detail.delivery.table.products.table.price'
            )
          "
          :template="priceTemplate"
          width="150"
        ></e-column>
        <e-column
          field="action"
          headerText=""
          width="40"
          :template="actionsTemplate"
        ></e-column>
      </e-columns>
    </ejs-grid>
  </div>
</template>

<script>
import { DetailRow } from "@syncfusion/ej2-vue-grids";
import { Query, Predicate } from "@syncfusion/ej2-data";
import { GetDataManagerNew } from "../../../../../../ds";
import OrdersTableAccount from "./OrdersTableAccount";
import OrdersTableName from "./OrdersTableName";
import OrdersTableInfo from "./OrdersTableInfo";
import OrdersTablePrice from "./OrdersTablePrice";
import OrdersTableActions from "./OrdersTableActions";
import OrdersTableRowDetails from "./OrdersTableRowDetails";
import EventBus from "../../../../../../helpers/EventBus";
import { buildPredicate } from "../../../../../../helpers/common";
import orderManagementMixin from "../../../share/mixin";
import { filtersMap } from "../../filtersMap";
import { PERMS } from "../../../../../../helpers/acl";
import OrdersPrints from "../../../share/OrdersPrints";
import { INCIDENT_TYPE } from "../../../../../../config/global";

export default {
  name: "OrdersTable",

  components: {
    OrdersPrints,
  },

  mixins: [orderManagementMixin],

  props: {
    ddmi: { type: Object, required: true },
  },

  provide: {
    grid: [DetailRow],
  },

  watch: {
    ddmi() {
      this.query = this.prepareQuery();
      this.itemSelected = Object.assign({});
    },
  },

  data: function () {
    const roleId = this.$store.state.role.id;
    const endpoint = filtersMap("ddmi.panel1.list.endpoint");
    const dm = GetDataManagerNew(endpoint, [roleId]);
    const query = this.prepareQuery();

    return {
      INCIDENT_TYPE,
      PERMS,
      dm: dm,
      query: query,
      selectionSettings: {
        persistSelection: false,
        enableToggle: true,
        checkboxOnly: true,
      },

      itemSelected: {},

      accountTemplate: () => {
        return { template: OrdersTableAccount };
      },

      nameTemplate: () => {
        return { template: OrdersTableName };
      },

      infoTemplate: () => {
        return { template: OrdersTableInfo };
      },

      priceTemplate: () => {
        return { template: OrdersTablePrice };
      },

      actionsTemplate: () => {
        return { template: OrdersTableActions };
      },

      detailTemplate: () => {
        return {
          template: {
            extends: OrdersTableRowDetails,
            propsData: {
              grid: this.$refs.grid,
              ddmi: this.ddmi,
            },
          },
        };
      },
    };
  },

  mounted() {
    EventBus.$on("orders:detail", this.onDetailsSelected);
  },

  beforeDestroy() {
    EventBus.$off("orders:detail", this.onDetailsSelected);
  },

  methods: {
    prepareQuery() {
      const { keyword } = this.$store.state.orders.filters;
      let predicate = new Predicate(
        "order.ddmi.id",
        "equal",
        this.ddmi.ddmi.id
      );

      const map = filtersMap("ddmi.panel1.list.filters");
      predicate = buildPredicate(
        predicate,
        map,
        this.$store.state.orders.filters
      );

      let query = new Query().where(predicate);

      const searchFields = filtersMap("ddmi.panel1.list.search", []);
      if (keyword && searchFields.length > 0) {
        query = query.search(keyword, searchFields);
      }

      return query;
    },

    getItemSelectedIdKey() {
      return "order.id";
    },

    getSelectedParentKey() {
      return "orders";
    },

    getSelectedChildrenKey() {
      return "details";
    },

    getMapSelectedToIdsMapping() {
      return { orders: this.getItemSelectedIdKey() };
    },

    createIncident(type) {
      const selected = this.getSelected();
      if (selected) {
        const entities = [];
        Object.keys(selected).forEach((type) => {
          entities.push({ type, items: [...selected[type]] });
        });

        this.$store.commit("incidents/createResetAllEntities");
        this.$store.commit("incidents/createAddEntities", {
          type: type,
          entities,
          show: true,
        });
      }
    },

    setDelivered() {
      const selected = this.getSelected();
      if (selected) {
        this.$store
          .dispatch("rounds/updateStatusBatch", {
            status: filtersMap("ddmiIsland.statusCode.next"),
            ...this.mapSelectedToIds(
              selected,
              this.getMapSelectedToIdsMapping()
            ),
          })
          .then(this.onUpdateStatusSuccess);
      }
    },

    setNotDelivered() {
      const selected = this.getSelected();
      if (selected) {
        this.$store
          .dispatch("rounds/updateStatusBatch", {
            status: filtersMap("ddmiIsland.statusCode.prev"),
            ...this.mapSelectedToIds(
              selected,
              this.getMapSelectedToIdsMapping()
            ),
          })
          .then(this.onUpdateStatusSuccess);
      }
    },

    print() {},

    canView(perm) {
      return this.$store.getters["connections/havePermission"](perm);
    },
  },
};
</script>
